import { all, call, put, select } from 'redux-saga/effects';
import { sagaCreator, stateSelector } from 'redux-saga-kit';
import {
  startOrderSuccess,
  startOrderError,
  startOrderRequest,
  fetchOrderHistory,
  fetchOrderHistorySuccess,
  cancelOrder,
  updateDeliveryType,
  orderItemChangeCount,
  toggleMenuFilter,
  updateDeliveryAddress,
  resetManagerDiscount
} from '../engine/actions/OrderActions';
import { openrestHelpers } from '@wix/wixrest-utils';
import _ from 'lodash';
import { getSupportedDeliveryTypes, parseComments } from '../../utils/OrderUtils';
import BILogger from '../../utils/BILogger';
import { Creditcards } from 'creditcards-info';
import { Gateways } from 'payments';
import { displayModal, fullOrganizationSuccess } from '../engine/actions/SessionActions';
import { getSimplifiedSectionByItemId } from '../../utils/MenuUtils';
import { Modals } from '../../utils/constants';
import { createI18nObject } from '../../utils/i18nUtils';
import RestaurantHelper from '@wix/wixrest-utils/dist/openrest-helpers/Restaurant';
import moment from 'moment-timezone';
import { deliveryTypeSelector } from '../engine/selectors/deliveryTypeSelector';
import { getItemErrors } from '../../utils/DishUtils';

const onStartOrderRequest = ({ restClient, PhoneUtils }) =>
  function*({ payload }) {
    const { phone } = payload;
    const organizationId = yield select(stateSelector('session.organization.id'));
    const countryCode = yield select(stateSelector('session.organization.address.countryCode'));
    const isValidPhone = !!PhoneUtils.validatePhone(phone, countryCode);

    if (isValidPhone) {
      try {
        const intPhone = PhoneUtils.internationalizePhone(phone, countryCode);
        const customerId = encodeURIComponent(`tel:${intPhone}`);

        const [callerInfo, comments] = yield all([
          call(getCustomer(restClient), customerId),
          call(getCustomerComments(restClient), customerId),
          put(fetchOrderHistory({ customerId })),
        ]);
        yield put(
          startOrderSuccess({
            callerInfo,
            comments: comments.parsedComments,
            commentsServerTimestamp: comments.commentsServerTimestamp,
          }),
        );
      } catch (e) {
        yield put(startOrderError({ error: e }));
      }
    } else {
      yield put(startOrderError({ error: 'phone not valid' }));
    }

    BILogger.phoneNumberClickContinue({ organizationId, isError: !isValidPhone, phoneError: '' });
  };

const getCustomer = restClient =>
  function*(customerId) {
    const organizationId = yield select(stateSelector('session.organization.id'));
    const accessToken = yield select(stateSelector('session.accessToken'));

    return yield call(restClient, `/organizations/${organizationId}/customers/${customerId}/account`, 'get', null, {
      type: 'Bearer',
      credentials: accessToken,
    });
  };

const getCustomerComments = restClient =>
  function*(customerId) {
    const organizationId = yield select(stateSelector('session.organization.id'));
    const accessToken = yield select(stateSelector('session.accessToken'));

    const response = yield call(
      restClient,
      `/organizations/${organizationId}/customers/${customerId}/secrets`,
      'get',
      null,
      {
        type: 'Bearer',
        credentials: accessToken,
      },
    );

    return {
      commentsServerTimestamp: response.modified,
      parsedComments: parseComments(response),
    };
  };

const onFetchOrderHistory = ({ restClient, i18n }) =>
  function*({ payload }) {
    const { customerId } = payload;
    const organizationId = yield select(stateSelector('session.organization.id'));
    const accessToken = yield select(stateSelector('session.accessToken'));
    const menuFull = yield select(stateSelector('session.menu'));
    const locale = yield select(stateSelector('session.organization.locale'));
    const organization = yield select(stateSelector('session.organization'));
    const i18nObj = createI18nObject(i18n.t.bind(i18n), locale);

    if (customerId) {
      const response = yield call(
        restClient,
        `/organizations/${organizationId}/orders?viewMode=restaurant&user=${customerId}&order=${encodeURIComponent(
          'modified:desc',
        )}&modified=${encodeURIComponent('gte:0')}`,
        'get',
        null,
        {
          type: 'Bearer',
          credentials: accessToken,
        },
      );
      const orders = _.get(response, 'results', []);

      const displayableOrders = orders.map(order => {
        const { id, created, delivery } = order;
        const displayableOrder = openrestHelpers.Order.getDisplayableOrder({
          order,
          menuFull,
          locale,
          organization,
          i18n: i18nObj,
          Creditcards,
          Gateways,
        });

        return {
          id,
          created,
          deliveryType: delivery.type,
          ...displayableOrder,
        };
      });

      yield put(fetchOrderHistorySuccess({ orders, displayableOrders }));
    }
  };

function* onCancelOrder() {
  const organization_id = yield select(stateSelector('session.organization.id'));
  BILogger.cancelOrderClick({ organization_id });
}

function* removeUnsupportedOrderItems() {
  const orderItems = yield select(stateSelector('order.orderItems'));
  const simplifiedMenu = yield select(stateSelector('session.simplifiedMenu'));
  const organization = yield select(stateSelector('session.organization'));
  const organizationDeliveryTypes = getSupportedDeliveryTypes(organization);
  let foundInvalidOrderItem = false;
  const itemsHash=yield select(stateSelector('session.itemsHash'))

  for (let i = orderItems.length; i--; i >= 0) {
    const orderItem = orderItems[i];
    const previousCount = orderItem.count || 1;
    const dish = simplifiedMenu.simplifiedItems.find(d => d.id === orderItem.itemId);
    const section = getSimplifiedSectionByItemId(simplifiedMenu, orderItem.itemId);
    const { deliveryTypes, platforms } = dish.condition;
    const dishDeliveryTypes = Object.keys(deliveryTypes).filter(dt => deliveryTypes[dt]);
    const intersection = _.intersection(organizationDeliveryTypes, dishDeliveryTypes);
    const deliveryType = deliveryTypeSelector(yield select());
    const errors = getItemErrors(itemsHash[orderItem.itemId], moment(), deliveryType, simplifiedMenu);
    const isUnsupportedDeliveryType = errors.find(error => error.type === 'error.deliveryType');

    if (
      _.isEmpty(intersection) ||
      !platforms.callcenter ||
      !section.condition.platforms.callcenter ||
      !deliveryTypes[deliveryType] ||
      isUnsupportedDeliveryType
    ) {
      yield put(orderItemChangeCount({ cartIndex: i, count: 0, previousCount }));
      foundInvalidOrderItem = true;
    }
  }

  return foundInvalidOrderItem;
}

function* onUpdateDeliveryType({ payload }) {
  yield call(removeUnsupportedOrderItems);

  const organizationId = yield select(stateSelector('session.organization.id'));
  BILogger.addressInformationChangeDeliveryType({ organizationId, deliveryType: payload.deliveryType });
}

function* onFullOrganizationSuccess() {
  const foundInvalidOrderItem = yield call(removeUnsupportedOrderItems);

  if (foundInvalidOrderItem) {
    yield put(displayModal({ modal: Modals.SETTINGS_CHANGED }));
  }
}

function* onToggleMenuFilter({ payload }) {
  const organizationId = yield select(stateSelector('session.organization.id'));

  BILogger.menuFilterOptions({
    organizationId,
    value: payload.filter,
  });
}

function* onUpdateDeliveryAddress({ payload }) {
  const { address } = payload;
  const organization = yield select(stateSelector('session.organization'));
  const addresses = yield select(stateSelector('order.callerInfo.addresses'));
  const deliveryInfo = RestaurantHelper.getDeliveryInfo(null, organization, address.latLng, null, null, moment);
  const isInDeliveryArea = !_.isEmpty(deliveryInfo) && deliveryInfo.type === 'delivery';
  const isNewAddress = !_.find(addresses, a => _.isEqual(a, address));

  BILogger.addressInformationFillChangeAddress({
    isInDeliveryArea,
    isNewAddress,
    organizationId: organization.id,
  });
}

function* onOrderItemChangeCount() {
  const hasNoItemsInCart = _.isEmpty(yield select(stateSelector('order.orderItems')));

  if (hasNoItemsInCart) {
    yield put(resetManagerDiscount());
  }
}

////////////// default export

export default sagaCreator({
  [startOrderRequest]: { handler: onStartOrderRequest },
  [fetchOrderHistory]: { handler: onFetchOrderHistory },
  [cancelOrder]: { handler: onCancelOrder, noParams: true },
  [updateDeliveryType]: { handler: onUpdateDeliveryType, noParams: true },
  [fullOrganizationSuccess]: { handler: onFullOrganizationSuccess, noParams: true },
  [toggleMenuFilter]: { handler: onToggleMenuFilter, noParams: true },
  [updateDeliveryAddress]: { handler: onUpdateDeliveryAddress, noParams: true },
  [orderItemChangeCount]: { handler: onOrderItemChangeCount, noParams: true },
});
